import React, { useMemo, useState } from 'react';
import TokenGroup from '@amzn/awsui-components-console/token-group';
import Select from '@amzn/awsui-components-console/select';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Box from '@amzn/awsui-components-console/box';
import {
	Dependancy,
	TaskDefinition,
} from 'src/stores/workflowDefinitionCanvasStore/workflowDefinitionCanvasStore.interface';
import { ReactFlowHelpNodeIDList, isTerminalTaskId } from 'src/components/createWorklow/customNode/customNodeEnums';
import { useStore } from 'src/stores/store';
import { Store } from 'src/stores/store.interface';

export interface DependencyEditorProps {
	taskId: string;
	dependencies: Dependancy[];
	allTasks: TaskDefinition[];
}

const START_OPTION: { label: string; value: string } = {
	label: 'START',
	value: ReactFlowHelpNodeIDList.START,
};

export function wouldCreateCycle(
	fromTaskId: string,
	toTaskId: string,
	dependencies: Dependancy[]
): boolean {
	const visited = new Set<string>();
	const queue = [toTaskId];

	while (queue.length > 0) {
		const current = queue.shift()!;

		if (current === fromTaskId) return true;
		if (visited.has(current)) continue;
		visited.add(current);
		dependencies
			.filter((d) => d.end === current)
			.forEach((d) => queue.push(d.start));
	}

	return false;
}

export default function DependencyEditor({
	taskId,
	dependencies,
	allTasks,
}: DependencyEditorProps) {
	const [selectedOption, setSelectedOption] = useState<{
		label: string;
		value: string;
	} | null>(null);

	const setWFDefinition = useStore((state: Store) => state.setWFDefinition);
	const saveToHistory = useStore((state: Store) => state.saveToHistory);

	const currentDeps = useMemo(
		() => dependencies.filter((d) => d.end === taskId),
		[dependencies, taskId]
	);

	const tokens = useMemo(
		() =>
			currentDeps.map((dep) => {
				const task = allTasks.find((t) => t.taskId === dep.start);
				const label = task?.taskName ?? dep.start;

				return {
					label,
					dismissLabel: `Remove ${label}`,
					value: dep.start,
				};
			}),
		[currentDeps, allTasks]
	);

	const availableOptions = useMemo(() => {
		const existingStartIds = new Set(currentDeps.map((d) => d.start));
		const hasRealTaskDep = currentDeps.some(
			(d) => !isTerminalTaskId(d.start)
		);

		const taskOptions = allTasks
			.filter((task) => {
				if (task.taskId === taskId) return false;
				if (existingStartIds.has(task.taskId)) return false;
				if (wouldCreateCycle(task.taskId, taskId, dependencies)) return false;

				return true;
			})
			.map((task) => ({
				label: task.taskName,
				value: task.taskId,
			}));

		const isEndTask = taskId === ReactFlowHelpNodeIDList.END;
		const hideStart =
			existingStartIds.has(START_OPTION.value) ||
			START_OPTION.value === taskId ||
			isEndTask ||
			hasRealTaskDep;
		const startOptions = hideStart ? [] : [START_OPTION];

		return [...startOptions, ...taskOptions];
	}, [allTasks, taskId, currentDeps, dependencies]);

	const updateStoreDependencies = (newDependencies: Dependancy[]) => {
		const currentWFDefinition = useStore.getState().WFDefinition;

		setWFDefinition({
			...currentWFDefinition,
			dependencies: newDependencies,
		});
		saveToHistory();
	};

	const handleAdd = (option: { label?: string; value?: string }) => {
		if (!option.value) return;
		const freshDeps = useStore.getState().WFDefinition.dependencies;
		const newDep: Dependancy = { start: option.value, end: taskId };
		let newDependencies = [...freshDeps, newDep];

		if (option.value !== ReactFlowHelpNodeIDList.START) {
			newDependencies = newDependencies.filter(
				(d) => !(d.start === ReactFlowHelpNodeIDList.START && d.end === taskId)
			);
		}

		updateStoreDependencies(newDependencies);
		setSelectedOption(null);
	};

	const handleRemove = (itemIndex: number) => {
		const depToRemove = currentDeps[itemIndex];

		if (!depToRemove) return;
		const freshDeps = useStore.getState().WFDefinition.dependencies;
		const newDependencies = freshDeps.filter(
			(d) => !(d.start === depToRemove.start && d.end === depToRemove.end)
		);

		updateStoreDependencies(newDependencies);
	};

	return (
		<SpaceBetween size="xs">
			<Box>
				<Select
					selectedOption={selectedOption}
					onChange={({ detail }) => {
						handleAdd(detail.selectedOption);
					}}
					options={availableOptions}
					placeholder="Add dependency..."
					empty="No available tasks"
					filteringType="auto"
					data-testid="dependency-editor-select"
				/>
			</Box>
			<TokenGroup
				items={tokens}
				onDismiss={({ detail: { itemIndex } }) => {
					handleRemove(itemIndex);
				}}
				data-testid="dependency-editor-tokens"
			/>
		</SpaceBetween>
	);
}
